export type TranscriptAnalysisQuality = "SUFFICIENT" | "PARTIAL" | "INSUFFICIENT_DATA";

export type TranscriptAnalysisBadgeVariant = "default" | "secondary" | "outline";

export interface TranscriptAnalysisPresentation {
  badgeLabel: string;
  badgeVariant: TranscriptAnalysisBadgeVariant;
  detailTitle: string | null;
  detailMessage: string | null;
  isRetryNeeded: boolean;
  isInsufficient: boolean;
}

export function getTranscriptAnalysisPresentation(args: {
  analysisQuality?: TranscriptAnalysisQuality | string | null;
  analysisReason?: string | null;
  transcriptDocumentContext?: {
    interactionMode?: string;
  } | null;
  qualitySignals?: {
    word_count?: number;
    speaker_turn_count?: number;
    unique_speaker_count?: number;
  } | null;
}): TranscriptAnalysisPresentation {
  const analysisQuality = args.analysisQuality ?? null;
  const analysisReason = normalizeAnalysisReason(args.analysisReason ?? null);
  const transcriptDocumentContext = args.transcriptDocumentContext ?? null;
  const qualitySignals = args.qualitySignals ?? null;
  const isRetryNeeded =
    analysisReason === "ai_malformed_response" || analysisReason === "ai_response_blocked";

  if (analysisQuality === "SUFFICIENT") {
    return {
      badgeLabel: "Sufficient Signal",
      badgeVariant: "default",
      detailTitle: null,
      detailMessage: null,
      isRetryNeeded: false,
      isInsufficient: false,
    };
  }

  if (analysisQuality === "PARTIAL") {
    return {
      badgeLabel: "Partial Signal",
      badgeVariant: "secondary",
      detailTitle: null,
      detailMessage: null,
      isRetryNeeded: false,
      isInsufficient: false,
    };
  }

  if (analysisQuality === "INSUFFICIENT_DATA") {
    return {
      badgeLabel: isRetryNeeded ? "System Retry Needed" : "Insufficient Signal",
      badgeVariant: "outline",
      detailTitle: isRetryNeeded ? "Analysis needs retry:" : "Why this was marked insufficient:",
      detailMessage: buildInsufficientMessage(
        analysisReason,
        transcriptDocumentContext,
        qualitySignals,
      ),
      isRetryNeeded,
      isInsufficient: true,
    };
  }

  return {
    badgeLabel: analysisQuality ?? "Unknown",
    badgeVariant: "outline",
    detailTitle: null,
    detailMessage: null,
    isRetryNeeded: false,
    isInsufficient: false,
  };
}

function normalizeAnalysisReason(reason: string | null): string | null {
  if (!reason) return null;

  if (
    reason === "quality_assessment_failed" ||
    reason === "analysis_unavailable" ||
    reason === "retry_upload"
  ) {
    return "ai_malformed_response";
  }

  return reason;
}

function buildInsufficientMessage(
  analysisReason: string | null,
  transcriptDocumentContext?: {
    interactionMode?: string;
  } | null,
  qualitySignals?: {
    word_count?: number;
    speaker_turn_count?: number;
    unique_speaker_count?: number;
  } | null,
): string {
  const hasUsableSignal =
    (qualitySignals?.word_count ?? 0) >= 120 ||
    (qualitySignals?.speaker_turn_count ?? 0) >= 4 ||
    transcriptDocumentContext?.interactionMode === "speaker_dominant";

  if (analysisReason === "transcript_too_short") {
    return "This transcript did not include enough total dialogue for reliable analysis.";
  }
  if (analysisReason === "too_few_speaker_turns") {
    return "This transcript did not include enough conversational turns for reliable analysis.";
  }
  if (analysisReason === "single_speaker_transcript") {
    return "This transcript appears to include only one speaker, so meeting analysis would be unreliable.";
  }
  if (analysisReason === "limited_dialogue_variety") {
    return "This transcript did not include enough back-and-forth across multiple speakers.";
  }
  if (analysisReason === "limited_leader_presence") {
    return "The selected leader did not have enough speaking presence in this sample for reliable analysis.";
  }
  if (analysisReason === "ai_malformed_response" || analysisReason === "ai_response_blocked") {
    if (transcriptDocumentContext?.interactionMode === "speaker_dominant") {
      return "We detected strong primary-speaker signal, but the analysis service returned an invalid response. This is a system issue, not a transcript-quality issue.";
    }
    if (hasUsableSignal) {
      return "We had enough transcript to analyze, but the analysis service returned an invalid response. This is a system issue, not a transcript-quality issue.";
    }
    return "The analysis service returned an invalid response. This is likely a system issue, not your transcript quality.";
  }
  return "The transcript lacked enough conversational signal for detailed analysis.";
}
